/**
 * SessionList — the sidebar's conversation list (Stage A): the active
 * product's sessions newest-activity-first under a "Recent" heading (the
 * "New chat" row moved up into the shell's product group, 2026-09-02). Each row carries its status
 * glyph on the right — a small spinner while its turn streams (writing), an
 * accent dot when it finished off-screen (unread) — and an inline kebab.
 *
 * The kebab offers Rename (PATCH /api/chat/sessions/{sid} {title} — Stage B
 * wired it) and Delete (confirm dialog; deleting the open session lands
 * back on the product home). Rename edits in place: the row swaps to an
 * input, Enter saves, Escape cancels.
 *
 * Kebab reveal: hover-capable pointers get it on row hover (and while its
 * menu is open); touch screens always see it.
 *
 * Round 5 added one quiet word to a row: a session whose answers were
 * written against code that has since been refreshed says "Older code" here,
 * because the sidebar is where you decide which conversation to reopen.
 */

import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

import { queryKeys, useDeleteSession, useSessions } from '../api/queries';
import type { ChatSessionSummary, Ulid } from '../api/types';
import { patchChatSession } from '../features/chat/chatApi';
import { productPath, sessionPath } from '../lib/deepLink';
import { Button, Dialog, ErrorNote, SkeletonLine } from '../lib/ui';
import { useSessionActivity, type SessionActivityStatus } from './sessionActivity';
import { attachTurn, isTurnActiveFor } from '../features/chat/streamController';

export function sessionTitle(s: Pick<ChatSessionSummary, 'title'>): string {
  return s.title ?? 'New chat';
}

/** Newest activity first (ISO-8601 compares lexicographically). */
export function sortSessions(sessions: ChatSessionSummary[]): ChatSessionSummary[] {
  return sessions.slice().sort((a, b) => b.updated_at.localeCompare(a.updated_at));
}

function StatusGlyph({ status }: { status: SessionActivityStatus | undefined }) {
  if (status === 'writing') {
    return (
      <span
        data-testid="session-status-writing"
        role="status"
        aria-label="The guide is writing"
        className="inline-block h-3 w-3 shrink-0 animate-spin rounded-pill border-2 border-(--border-strong) border-t-(--accent) motion-reduce:animate-none"
      />
    );
  }
  if (status === 'unread') {
    return (
      <span
        data-testid="session-status-unread"
        aria-label="New reply"
        className="inline-block h-1.5 w-1.5 shrink-0 rounded-pill bg-(--accent)"
      />
    );
  }
  return null;
}

/**
 * This conversation reads older code than the codebase has now (round 5,
 * owner: "What about for different sessions?").
 *
 * A session opened tomorrow should say up front that what is already in it
 * describes the code as it was, before you get three answers deep and
 * wonder why a file it names is not there any more. Deliberately NOT an
 * alarm colour: nothing is broken, the next question still answers against
 * the newest code, and a red dot in a sidebar reads as something to fix.
 */
function StaleBadge() {
  return (
    <span
      data-testid="session-stale"
      title="The code changed after these answers were written. Your next question here uses the newest code."
      className="shrink-0 rounded-pill border border-default px-1.5 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary"
    >
      Older code
    </span>
  );
}

/** Kebab reveal: hidden until hover on hover-capable pointers, else shown. */
const KEBAB_REVEAL =
  '[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:focus-visible:opacity-100';

function SessionRow({
  productId,
  session,
  status,
  onDelete,
}: {
  productId: Ulid;
  session: ChatSessionSummary;
  status: SessionActivityStatus | undefined;
  onDelete: (session: ChatSessionSummary) => void;
}) {
  const qc = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (renaming) inputRef.current?.focus();
  }, [renaming]);

  const startRename = () => {
    setMenuOpen(false);
    setDraft(sessionTitle(session));
    setRenaming(true);
  };

  const commitRename = () => {
    const title = draft.trim();
    if (saving) return;
    if (title.length === 0 || title === sessionTitle(session)) {
      setRenaming(false);
      return;
    }
    setSaving(true);
    patchChatSession(session.id, { title })
      .then(() => {
        void qc.invalidateQueries({ queryKey: queryKeys.sessions(productId) });
        void qc.invalidateQueries({ queryKey: queryKeys.session(session.id) });
        setRenaming(false);
      })
      .catch(() => {
        // Keep the input open — nothing was saved; the draft stays editable.
      })
      .finally(() => setSaving(false));
  };

  if (renaming) {
    return (
      <div className="px-1 py-0.5">
        <input
          ref={inputRef}
          data-testid="session-rename-input"
          aria-label="Rename conversation"
          value={draft}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename();
            if (e.key === 'Escape') setRenaming(false);
          }}
          className="h-7 w-full rounded-card border border-(--accent) bg-inset px-2 text-[length:var(--text-ui-size)] text-primary outline-none"
        />
      </div>
    );
  }

  return (
    <div ref={rootRef} className="group relative">
      <NavLink
        to={sessionPath(productId, session.id)}
        data-testid="session-row"
        data-session={session.id}
        // The row's own status, readable from the outside: the sidebar is
        // how a background answer announces itself, so it is worth being
        // able to assert on.
        data-activity={status ?? 'seen'}
        title={sessionTitle(session)}
        className={({ isActive }) =>
          `relative flex items-center gap-2 rounded-card py-1.5 pr-7 pl-2.5 no-underline transition-colors duration-(--motion-hover) ${
            isActive
              ? 'bg-accent-subtle text-primary'
              : 'text-secondary hover:bg-inset hover:text-primary'
          }`
        }
      >
        {({ isActive }) => (
          <>
            {isActive ? (
              <span
                aria-hidden
                data-testid="session-active-bar"
                className="absolute inset-y-1 left-0 w-[2px] rounded-pill bg-(--accent)"
              />
            ) : null}
            <span className="min-w-0 flex-1 truncate text-[length:var(--text-ui-size)]">
              {sessionTitle(session)}
            </span>
            {session.staleness?.stale === true ? <StaleBadge /> : null}
            <StatusGlyph status={status} />
          </>
        )}
      </NavLink>
      <button
        type="button"
        data-testid="session-kebab"
        title="Conversation actions"
        aria-label={`Actions for ${sessionTitle(session)}`}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        onClick={() => setMenuOpen((o) => !o)}
        className={`absolute top-1/2 right-1 flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded-chip border-none bg-transparent text-tertiary transition-opacity duration-(--motion-hover) hover:text-primary ${
          menuOpen ? 'opacity-100' : KEBAB_REVEAL
        }`}
      >
        <span aria-hidden>⋯</span>
      </button>
      {menuOpen ? (
        <div
          data-testid="session-menu"
          role="menu"
          className="absolute top-full right-1 z-50 w-36 rounded-card border border-default bg-raised p-1 shadow-overlay"
        >
          <button
            type="button"
            role="menuitem"
            data-testid="session-rename"
            onClick={startRename}
            className="flex w-full cursor-pointer items-center rounded-chip border-none bg-transparent px-2 py-1.5 text-left text-[length:var(--text-ui-size)] text-secondary transition-colors duration-(--motion-hover) hover:bg-inset hover:text-primary"
          >
            Rename
          </button>
          <button
            type="button"
            role="menuitem"
            data-testid="session-delete"
            onClick={() => {
              setMenuOpen(false);
              onDelete(session);
            }}
            className="flex w-full cursor-pointer items-center rounded-chip border-none bg-transparent px-2 py-1.5 text-left text-[length:var(--text-ui-size)] text-secondary transition-colors duration-(--motion-hover) hover:bg-inset hover:text-(--danger)"
          >
            Delete
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function SessionList({ productId }: { productId: Ulid }) {
  const navigate = useNavigate();
  const { sid: routeSid } = useParams<'sid'>();
  const sessions = useSessions(productId);
  // Rows the server says are still writing (a reload mid-answer, another
  // tab's question) get a pump here too, so the spinner is honest and the
  // answer lands as unread instead of as a dead turn (2026-09-07).
  const attachQc = useQueryClient();
  const writingIds = (sessions.data?.sessions ?? [])
    .filter((s) => s.active_generation === true)
    .map((s) => s.id)
    .join(',');
  useEffect(() => {
    if (writingIds.length === 0) return;
    for (const id of writingIds.split(',')) {
      if (!isTurnActiveFor(id)) void attachTurn(attachQc, id);
    }
  }, [attachQc, writingIds]);
  const statuses = useSessionActivity((s) => s.statuses);
  const deleteSession = useDeleteSession(productId);
  const [deleting, setDeleting] = useState<ChatSessionSummary | null>(null);

  const ordered = sortSessions(sessions.data?.sessions ?? []);

  // Teach the activity store which product owns these sessions (the
  // switcher's cross-product dot needs the mapping).
  const sessionData = sessions.data;
  useEffect(() => {
    if (sessionData === undefined) return;
    const record = useSessionActivity.getState().recordProduct;
    for (const s of sessionData.sessions) record(s.id, productId);
  }, [sessionData, productId]);

  const closeDelete = () => {
    deleteSession.reset();
    setDeleting(null);
  };
  const confirmDelete = () => {
    if (deleting === null) return;
    const sid = deleting.id;
    deleteSession.mutate(sid, {
      onSuccess: () => {
        closeDelete();
        if (routeSid === sid) void navigate(productPath(productId));
      },
    });
  };

  return (
    <div data-testid="session-list" className="flex min-h-0 flex-1 flex-col">
      {ordered.length > 0 ? (
        <div
          aria-hidden
          data-testid="session-list-heading"
          className="px-2.5 pt-2 pb-1 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] font-medium tracking-[var(--text-micro-tracking)] text-tertiary uppercase"
        >
          Recent
        </div>
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto" data-testid="session-rows">
        {sessions.isPending ? (
          // Three row-shaped ghosts while the list loads: the sidebar keeps
          // its silhouette instead of flashing empty and then filling.
          <div data-testid="session-list-pending" className="space-y-1.5 px-2.5 py-1.5">
            <SkeletonLine height={20} />
            <SkeletonLine height={20} width="85%" />
            <SkeletonLine height={20} width="70%" />
          </div>
        ) : sessions.isError ? (
          <div data-testid="session-list-error" className="px-2.5 py-1.5">
            <p className="mt-0 mb-1.5 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary">
              Your conversations didn&rsquo;t load.
            </p>
            <button
              type="button"
              data-testid="session-list-retry"
              onClick={() => void sessions.refetch()}
              className="h-6 cursor-pointer rounded-pill border border-default bg-transparent px-2 text-[length:var(--text-micro-size)] text-secondary transition-colors duration-(--motion-hover) hover:border-(--accent) hover:text-primary"
            >
              Try again
            </button>
          </div>
        ) : (
          ordered.map((s) => (
            <SessionRow
              key={s.id}
              productId={productId}
              session={s}
              status={statuses[s.id]}
              onDelete={setDeleting}
            />
          ))
        )}
      </div>

      <Dialog open={deleting !== null} onClose={closeDelete} title="Delete this conversation?">
        {deleting !== null ? (
          <div className="space-y-4" data-testid="delete-session-dialog">
            <p className="mt-0 text-secondary">
              Deleting <span className="font-medium text-primary">{sessionTitle(deleting)}</span>{' '}
              removes its transcript for good. This cannot be undone.
            </p>
            <ErrorNote>{deleteSession.isError ? deleteSession.error.message : null}</ErrorNote>
            <div className="flex justify-end gap-2">
              <Button kind="ghost" onClick={closeDelete}>
                Keep it
              </Button>
              <Button kind="danger" disabled={deleteSession.isPending} onClick={confirmDelete}>
                {deleteSession.isPending ? 'Deleting…' : 'Delete conversation'}
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
